import {
    Button,
    InlineAlert,
    Link,
    Pane,
    ShareIcon,
    Spinner,
    Table,
    Text,
    TickCircleIcon
} from "evergreen-ui";
import { TundraStorefrontValidateCustomDomainInput } from "../../../../common/inputs/TundraStorefrontInput";
import { TundraStorefrontDomain } from "../../../../common/models";

import "./styles/StoreTundraAdminGetView.css";
import { isInternalDomain } from "./etc/DomainUtils";
import {
    StatusIndicator,
    TundraStorefrontStatus,
    TundraStorefrontStatusResolver
} from "./etc/TundraStorefrontStatusResolver";

export interface StoreTundraAdminDomainCardProps {
    domain: TundraStorefrontDomain;
    subDomain?: TundraStorefrontDomain;
    validateCustomDomain: (domain: TundraStorefrontValidateCustomDomainInput) => void;
    validateCustomDomainLoading: boolean;
    lastRefreshedAt: Date;
}

const PROCESSING_INDICATOR: StatusIndicator = {
    status: "Processing",
    details: "The certificate request is being processed.",
    intent: "none"
};

export const StoreTundraAdminDomainCard = (props: StoreTundraAdminDomainCardProps): JSX.Element => {

    const {
        domain,
        subDomain,
        validateCustomDomain,
        validateCustomDomainLoading,
        lastRefreshedAt
    } = props;

    const renderRow = (title: string, indicator?: StatusIndicator | null, showSpinner: boolean = false): JSX.Element | null => {
        return indicator ? <Table.Row height={"auto"}>
            <Table.Cell flexBasis={"33%"} flexShrink={0} flexGrow={0}>
                <p>
                    <b>{title}</b>
                </p>
            </Table.Cell>
            <Table.Cell flexBasis={"20%"} flexShrink={0} flexGrow={0}>
                <Pane display={"flex"} alignItems={"center"}>
                    { showSpinner && <Spinner size={12} marginRight={8}/> }
                    <InlineAlert
                        fontSize={8}
                        intent={indicator.intent}
                    >
                        { indicator.status }
                    </InlineAlert>
                </Pane>
            </Table.Cell>
            <Table.Cell>
                <p>
                    { indicator.details }
                </p>
            </Table.Cell>
        </Table.Row> : null;
    };

    // An in-flight validation invalidates every SSL value on screen, so show the row as
    // processing rather than leaving the pre-mutation value up.
    const renderSslRow = (title: string, indicator?: StatusIndicator | null, showSpinner: boolean = false): JSX.Element | null => {
        if (validateCustomDomainLoading && indicator) {
            return renderRow(title, PROCESSING_INDICATOR, true);
        }

        return renderRow(title, indicator, showSpinner);
    };

    const showValidationButton = (domain: TundraStorefrontDomain, status: StatusIndicator | null): boolean => {
        if (isInternalDomain(domain.alias.alias)) {
            return false;
        }

        return status?.intent !== "success";
    };

    const status: TundraStorefrontStatus = new TundraStorefrontStatusResolver(domain, subDomain).status;
    const certificateDeploying: boolean = domain.alias.lastCertificateRenewal === "DEPLOYING";
    return (
        <Pane
            className={"tundra-admin-domains-container-item"}
        >
            <div style={{
                display: "flex"
            }}>
                <div>
                    <Link href={`https://${domain.alias.alias}`} target={"_blank"}>
                        <div style={{
                            color: "var(--green-primary)",
                            display: "flex"
                        }}>
                            <h3
                                style={{
                                    color: "var(--green-primary)",
                                    margin: 0,
                                    paddingBottom: "8px",
                                    fontWeight: "bold"
                                }}
                            >
                                {domain.alias.alias}
                            </h3>
                            <ShareIcon marginLeft={8}/>
                        </div>
                    </Link>
                    { subDomain && <h5
                        style={{
                            margin: 0,
                            paddingBottom: "8px",
                            color: "grey"
                        }}
                    >
                        {subDomain.alias.alias}
                    </h5> }
                    <Text size={300} color={"muted"}>
                        {`Last refreshed ${lastRefreshedAt.toLocaleTimeString("en-US", { timeZone: "America/New_York" })} ET`}
                    </Text>
                </div>
                { showValidationButton(domain, status.ssl.validationStatus) && <Button
                    appearance={"primary"}
                    intent={"none"}
                    className={"tundra-admin-button"}
                    style={{
                        margin: "0 8px 4px 32px"
                    }}
                    isLoading={validateCustomDomainLoading}
                    iconBefore={TickCircleIcon}
                    onClick={() => validateCustomDomain({
                        primaryDomain: domain.alias.alias
                    })}
                >
                    Validate Certificate?
                </Button> }
            </div>
            <Table>
                <Table.Head>
                    <Table.HeaderCell flexBasis={"33%"} flexShrink={0} flexGrow={0}>
                        Component
                    </Table.HeaderCell>
                    <Table.HeaderCell flexBasis={"20%"} flexShrink={0} flexGrow={0}>
                        Status
                    </Table.HeaderCell>
                    <Table.HeaderCell>
                        Details
                    </Table.HeaderCell>
                </Table.Head>
                <Table.Body>
                    { renderRow(`DNS Record(s)`, status.dns.root) }
                    { renderRow(`Scan Status`, status.scan.root.status) }
                    { renderRow(`Scan Time`, status.scan.root.time) }
                    { renderRow(`www. DNS Record(s)`, status.dns.www) }
                    { renderRow(`www. Scan Status`, status.scan.www?.status) }
                    { renderRow(`www. Scan Time`, status.scan.www?.time) }
                    { renderSslRow("SSL Certificate Request Status", status.ssl.validationStatus) }
                    { renderSslRow("SSL Certificate Status", status.ssl.lastRenewStatus, certificateDeploying) }
                    { renderSslRow("SSL Certificate Expiration Time", status.ssl.expirationTime) }
                </Table.Body>
            </Table>
        </Pane>
    );

};
